import { defaultKeymap } from '@codemirror/commands';
import { LanguageSupport, StreamLanguage } from '@codemirror/language';
import { Extension } from '@codemirror/state';
import { DecorationSet, EditorView, keymap } from '@codemirror/view';
import { BracketsEllipsesIcon } from '@langchain/untitled-ui-icons';
import { tags as t } from '@lezer/highlight';
import { Alert, Option, Select, useTheme } from '@mui/joy';
import Card from '@mui/joy/Card';
import { SxProps } from '@mui/joy/styles/types';
import { alpha } from '@mui/system';
import { tokyoNightDayInit } from '@uiw/codemirror-theme-tokyo-night-day';
import { tokyoNightStormInit } from '@uiw/codemirror-theme-tokyo-night-storm';
import CodeMirror from '@uiw/react-codemirror';

import {
  ReactNode,
  forwardRef,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { MinimalCodeContext } from '@/Pages/Run/utils/MinimalCodeContext';
import { customGutterStyle } from '@/Pages/Run/utils/customGutterStyle';
import { IconButton } from '@/design-system';
import { useColorScheme } from '@/hooks/useColorScheme';
import { useLanguageContext } from '@/hooks/useLanguagePreferenceContext/LanguageContext';
import { cn } from '@/utils/tailwind';
import { tryParseJSON } from '@/utils/try-parse-json';
import { jsonSchemaExtensions } from '@/utils/use-codemirror-tooltip-follow-mouse';

import { CopyButton } from '../CopyButton/CopyButton';
import { CopyInlineLink } from '../CopyInlineLink';
import { ErrorBoundary } from '../ErrorBoundary';
import { ExpandableText } from '../ExpandableText';
import { PlainTextEditor } from '../PlainTextEditor';
import { DATA_CONVERTERS, useEditorTheme } from './Code.utils';
import {
  decorationStateField,
  imageReplacerPlugin,
} from './ImageReplacerExtension';
import { MinimalCode } from './MinimalCode';
import { MAX_CODE_LENGTH, MAX_TEXT_LENGTH } from './constants';
import { CodeLanguageType } from './types';

// MinimalViewButton component to toggle back to minimal view
function MinimalViewButton({ onClick }: { onClick: () => void }) {
  return (
    <div
      className="absolute right-10 top-2 z-10 bg-background opacity-0 transition-opacity duration-300 group-hover:opacity-100"
      data-testid="show-minimal"
    >
      <IconButton
        variant="outlined"
        size="sm"
        color="secondary"
        onClick={onClick}
        icon={BracketsEllipsesIcon}
        label="Show minimal editor for a minimal view"
        tooltipProps={{
          side: 'bottom',
        }}
      />
    </div>
  );
}

export interface CodeProps {
  language: LanguageSupport | StreamLanguage<unknown>;
  extensions?: Extension[];
  value: string | undefined;
  diffValues?: {
    decoration: DecorationSet;
    generatedCode: string | undefined;
  };
  onChange?: (code: string) => void;
  readOnly?: boolean;
  sx?: SxProps;
  autoFocus?: boolean;
  maxHeight?: string;
  omitMaxHeight?: boolean;
  width?: string;
  fontSize?: string;
  placeholder?: string;
  showCopyButton?: boolean;
  testId?: string;
  variant?: 'plain' | 'full';
  onCreateEditor?: (view: EditorView) => void;
  converterExtensions?: Extension[];
}

export const Code = ({
  value,
  diffValues,
  readOnly,
  onChange,
  autoFocus,
  placeholder,
  language,
  extensions,
  converterExtensions,
  showCopyButton,
  maxHeight,
  omitMaxHeight,
  width,
  fontSize,
  onCreateEditor,
  variant = 'full',
  testId,
}: CodeProps) => {
  const { isDarkMode } = useColorScheme();
  const editorTheme = useEditorTheme(!!diffValues, variant);

  const [ignoreWarning, setIgnoreWarning] = useState(false);

  const { decoration, generatedCode } = diffValues ?? {};

  const tokyoNightStorm = tokyoNightStormInit({
    styles: [
      {
        tag: t.content,
        color: '#9ece6a',
      },
    ],
  });

  const tokyoNightDay = tokyoNightDayInit({
    styles: [
      {
        tag: t.content,
        color: '#587539',
      },
    ],
  });

  const lineCount = value?.split('\n').length ?? 0;
  if (value == null) return null;

  // TODO: temporarily disable editor until we can
  // optimize CodeMirror to avoid causing huge dropoff in performance
  // due to recomputation of style
  // https://langchain.slack.com/archives/C06EASPPQNB/p1708379399679279
  if (lineCount > MAX_CODE_LENGTH && !ignoreWarning)
    return (
      <Alert
        color="warning"
        variant="soft"
        className="!rounded-none !rounded-t-[7px]"
      >
        <span>
          This content might be too long to display in the editor.{' '}
          <a
            className="cursor-pointer underline"
            onClick={() => setIgnoreWarning(true)}
          >
            Open anyway
          </a>
        </span>
      </Alert>
    );

  const mExtensions = [
    keymap.of([{ key: 'Mod-Enter', run: () => true }, ...defaultKeymap]),
    language,
    ...(extensions ?? []),
    // Our yaml folding extension is slow for large files and blocks ui rendering,
    // so disable it for text over 500 lines
    ...(lineCount <= 500 ? converterExtensions ?? [] : []),
    imageReplacerPlugin,
    decorationStateField,
    ...(decoration ? [EditorView.decorations.of(decoration)] : []),
    EditorView.lineWrapping,
    editorTheme,
    ...(readOnly ? [EditorView.editable.of(false)] : []),
  ];

  const bgColor = isDarkMode ? '#1a1a1a' : '#fafafa';

  return (
    <div data-testid={testId}>
      <ErrorBoundary fallback={() => <div>Error showing code</div>}>
        <CodeMirror
          key={generatedCode}
          value={generatedCode ?? value}
          readOnly={readOnly}
          autoFocus={autoFocus}
          placeholder={placeholder}
          maxHeight={omitMaxHeight ? undefined : maxHeight ?? '50vh'}
          width={width}
          style={{
            fontSize: fontSize,
            backgroundColor: bgColor,
            borderRadius: '6px',
            cursor: readOnly ? 'default' : 'text',
          }}
          extensions={mExtensions}
          onCreateEditor={onCreateEditor}
          onChange={(code) => {
            if (!readOnly) {
              onChange?.(code);
            }
          }}
          theme={isDarkMode ? tokyoNightStorm : tokyoNightDay}
          basicSetup={{
            highlightActiveLine: false,
            defaultKeymap: false,
            ...(variant === 'plain' && { lineNumbers: false }),
          }}
        />
      </ErrorBoundary>
      {showCopyButton && (
        <CopyButton copy={value} className="absolute right-2 top-2" />
      )}
    </div>
  );
};

export function CodeText(props: {
  value: string | undefined;
  onChange?: (code: string) => void;
  readOnly?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  className?: string;
  testId?: string;
  width?: string;
  omitMaxHeight?: boolean;
  omitOverflow?: boolean;
  removePadding?: boolean;
  truncate?: boolean;
}) {
  return (
    <div
      className={cn(
        !props.omitOverflow && 'overflow-auto',
        !props.omitMaxHeight && 'max-h-[50vh]',
        props.className
      )}
      style={{ width: props.width }}
      data-testid={props.testId}
    >
      <div style={{ overflowAnchor: 'none' }}>
        <PlainTextEditor
          truncate={props.truncate}
          className={cn(!props.removePadding && 'p-4')}
          value={props.value}
          onChange={props.onChange}
          readOnly={props.readOnly}
          autoFocus={props.autoFocus}
          placeholder={props.placeholder}
        />
      </div>
      <div className="h-px" style={{ overflowAnchor: 'auto' }} />
    </div>
  );
}

export function CodeDataReadOnly<T>(props: {
  value: T;
  language: CodeLanguageType;
  className?: string;
  omitMaxHeight?: boolean;
}) {
  const converter = DATA_CONVERTERS[props.language];
  const jsonValue = JSON.stringify(props.value);
  const editorValue = converter.stringify(tryParseJSON(jsonValue));

  return (
    <pre
      className={cn(
        'flex-wrap overflow-y-auto whitespace-pre-wrap break-words font-mono',
        !props.omitMaxHeight && 'max-h-[50vh]',
        props.className
      )}
    >
      <ExpandableText
        text={editorValue}
        initialMaxLength={MAX_TEXT_LENGTH}
        incrementLength={5000}
      />
    </pre>
  );
}

export function CodeDataEditor<T>(
  props: Omit<CodeProps, 'value' | 'language' | 'onChange'> & {
    value: T;
    onChange?: (value: T) => void;
    language: CodeLanguageType;
    children?: ReactNode;
    onParseErrorChange?: (error: string | null) => void;
  }
) {
  const [parseError, setParseError] = useState<string | null>(null);
  const [showFull, setShowFull] = useState(false);

  const { minimalCode } = useContext(MinimalCodeContext);

  const lastUserLang = useRef<typeof props.language>(props.language);

  const converter = DATA_CONVERTERS[props.language];
  const convertedValue = converter.stringify(props.value);

  const [editorValue, setEditorValue] = useState<string>(convertedValue);

  const debouncedParentOnChange = useDebouncedCallback((newValue: T) => {
    props.onChange?.(newValue);
  }, 100);

  useEffect(() => {
    if (props.language !== lastUserLang.current) {
      const newConverter = DATA_CONVERTERS[props.language];
      setEditorValue(newConverter.stringify(props.value));
      setParseError(null);
      props.onParseErrorChange?.(null);

      lastUserLang.current = props.language;
    }
  }, [props.language, props.value]);

  // Define this function outside any conditionals to keep hook order consistent
  function onEditorChange(value: string) {
    setEditorValue(value);

    try {
      const newValue = converter.parse<T>(value);
      debouncedParentOnChange(newValue);
      setParseError(null);
      props.onParseErrorChange?.(null);
    } catch (error) {
      const errorString = error?.toString() ?? 'Error while parsing';
      setParseError(errorString);
      props.onParseErrorChange?.(errorString);
    }
  }

  // Use a variable to determine what to render
  const showMinimalCode =
    minimalCode && props.readOnly && !showFull && !props.diffValues;

  return (
    <>
      {showMinimalCode ? (
        <MinimalCode
          code={props.readOnly ? convertedValue : editorValue}
          language={props.language}
          setShowFull={setShowFull}
          fullHeight={props.omitMaxHeight}
        />
      ) : (
        <div className="group relative">
          <Code
            {...props}
            value={props.readOnly ? convertedValue : editorValue}
            language={converter.language}
            extensions={props.extensions}
            converterExtensions={converter.extensions}
            onChange={onEditorChange}
          />
          {minimalCode && props.readOnly && !props.diffValues && (
            <MinimalViewButton onClick={() => setShowFull(false)} />
          )}
        </div>
      )}
      {props.children}
      {parseError && <Alert>{parseError}</Alert>}
    </>
  );
}

export function CodeLanguageSelect(props: {
  value: CodeLanguageType;
  onChange: (value: CodeLanguageType) => void;
}) {
  return (
    <Select
      value={props.value}
      onChange={(_, value) => {
        if (value != null) props.onChange(value);
      }}
      size="sm"
      slotProps={{
        root: {
          sx: {
            marginTop: 1,
            marginBottom: 1,
            marginLeft: 1,
            width: 'unset',
            backgroundColor: 'transparent',
            border: 'none',
            fontSize: 'small',
          },
        },
        button: {
          sx: (theme) => ({
            color: theme.vars.palette.neutral[500],
            fontWeight: 600,
            textTransform: 'uppercase',
          }),
        },
      }}
    >
      <Option value="json">JSON</Option>
      <Option value="yaml">YAML</Option>
    </Select>
  );
}

export function OpenRawButtonGroup<T>(props: {
  language: CodeLanguageType;
  value: T;
}) {
  const converter = DATA_CONVERTERS[props.language];

  const convertedValue = converter.stringify(props.value);

  const handleOpenInNewTab = () => {
    const blob = new Blob([convertedValue], {
      type: props.language === 'yaml' ? 'text/plain' : 'application/json',
    });
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
    // Clean up the blob URL after opening
    setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
  };

  return (
    <div className="flex items-center pr-3 text-sm font-medium text-gray-500">
      <button
        type="button"
        onClick={handleOpenInNewTab}
        className="rounded-lg p-1.5 hover:bg-tertiary"
      >
        RAW
      </button>
      <div className="cursor-pointer rounded-lg p-1.5 hover:bg-tertiary">
        <CopyInlineLink
          value={convertedValue}
          truncate={50}
          prefix="Raw"
        ></CopyInlineLink>
      </div>
    </div>
  );
}

export function CodeData<T>(
  props: Omit<CodeProps, 'value' | 'language' | 'onChange' | 'extensions'> & {
    value: T;
    onChange?: (value: T) => void;
    actions?: ReactNode;
    language?: CodeLanguageType;
    schema?: Record<string, unknown>;
  }
) {
  const [userLang, setUserLang] = useLanguageContext();
  const codeDataExtensions = useMemo(() => {
    if (props.schema && userLang === 'json') {
      return jsonSchemaExtensions(props.schema);
    }
    return [customGutterStyle];
  }, [props.schema]);

  return (
    <CodeDataEditor
      {...props}
      extensions={codeDataExtensions}
      language={
        props.language ?? (props.variant === 'plain' ? 'yaml' : userLang)
      }
    >
      {props.variant !== 'plain' && (
        <div className="flex justify-between border-t border-secondary">
          {props.actions ? (
            <>
              <div className="flex items-center gap-2">
                <CodeLanguageSelect value={userLang} onChange={setUserLang} />
                <OpenRawButtonGroup
                  language={props.language ?? userLang}
                  value={props.value}
                />
              </div>
              {props.actions}
            </>
          ) : (
            // If no actions, spread them across the container
            <>
              <CodeLanguageSelect value={userLang} onChange={setUserLang} />
              <OpenRawButtonGroup
                language={props.language ?? userLang}
                value={props.value}
              />
            </>
          )}
        </div>
      )}
    </CodeDataEditor>
  );
}

// TODO: restyle this component to match the new design
export const CodeCard = forwardRef<
  HTMLDivElement,
  {
    sx?: SxProps;
    children: ReactNode;
    variant?: 'output' | 'reference';
  }
>((props, ref) => {
  const theme = useTheme();

  let backgroundColor = theme.palette.background.level1;

  if (props.variant === 'output') {
    backgroundColor = alpha(theme.palette.success[400], 0.2);
  }

  if (props.variant === 'reference') {
    backgroundColor = alpha(theme.palette.primary[400], 0.2);
  }

  return (
    <Card
      ref={ref}
      size="md"
      sx={[
        {
          gap: 0,
          padding: 0,
          backgroundColor,
          boxShadow: 'none',
          ':focus-within': {
            outline: 'thin solid',
            outlineWidth: '2px',
            outlineColor: theme.palette.primary[500],
          },
        },
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
      ]}
    >
      {props.children}
    </Card>
  );
});
